import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
} from '@mui/material';
import SelectPublicDataPool from '../common/SelectPublicDataPool';
import SelectPublicTrainingSet from '../common/SelectPublicTrainingSet';
import SelectTrainingSet from '../common/SelectTrainingSet';
import SelectTrainingSetWithSearch from '../extractionBatches/create/SelectTrainingSetWithSearch';
import errorResponses from '../../errorResponses';
import useGlynt from '../hooks/useGlynt';
import { AppContext } from '../../App';
import { IDataPool, IField, ITrainingSet } from '../../types/my-types';
import { useParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { refreshCertification } from '../../utils/certificationCache';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import IconButtonWithTooltip from '../common/IconButtonWithTooltip';
import IconButtonWhiteBg from '../common/IconButtonWhiteBg';

const ImportFields: React.FC<{ iconType: 'button' | 'icon' | 'iconWhiteBg' }> = ({ iconType = 'button' }) => {
  const { dataPoolId, dataPoolLabel, setToast } = React.useContext(AppContext);
  const { id } = useParams<{ id: string }>();
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [fromTrainingSet, setFromTrainingSet] = React.useState<ITrainingSet>();
  const [fromDataPool, setFromDataPool] = React.useState<IDataPool>();
  const client = useQueryClient();
  const glynt = useGlynt();

  // Wrapper to ignore undefined values during loading (prevents flickering)
  const handleSetTrainingSet = (ts: ITrainingSet | undefined) => {
    if (ts) {
      setFromTrainingSet(ts);
    }
  };

  // Reset training set when data pool changes
  React.useEffect(() => {
    setFromTrainingSet(undefined);
  }, [fromDataPool?.id]);

  const importFields = useMutation({
    mutationFn: async (params: any) =>
      (await glynt).trainingSet.importFields({
        source_training_set_id: params.sourceTrainingSetId,
        dest_training_set_id: params.destTrainingSetId,
      }),

    onSuccess: (resp) => {
      refreshCertification(client, id);
      client.invalidateQueries({
        queryKey: ['fields']
      });

      const successResponse =
        `Imported ${resp.imported_fields} from ${resp.from.name}` ||
        'Fields imported successfully';
      setToast({
        open: true,
        message: successResponse,
        variant: 'success',
      });
    },

    onError: () => {
      setToast({
        open: true,
        message: errorResponses.failedToImportFields,
        variant: 'error',
      });
    }
  });

  function handleClickOpen() {
    setOpen(true);
  }

  function handleClose() {
    setOpen(false);
  }

  const handleOk = async () => {
    if (fromTrainingSet?.id) {
      setLoading(true);

      try {
        importFields.mutate({ destTrainingSetId: id, sourceTrainingSetId: fromTrainingSet.id });
      } catch (e) {
        console.error('Error importing fields: ', e);
        setToast({
          open: true,
          message: errorResponses.failedToImportFields,
          variant: 'error',
        });
      }

      setLoading(false);
      setOpen(false);
    }
  };

  const renderIcon = () => {
    if (iconType === 'iconWhiteBg') {
      return (
        <IconButtonWhiteBg
          disabled={false}
          onClick={handleClickOpen}
          id="icon-import-fields"
          cypressId="btn-import-fields"
          tooltip="Import Fields"
        >
          <ImportExportIcon style={{ fontSize: '14px', color: 'black' }} />
        </IconButtonWhiteBg>
      );
    }
    if (iconType === 'icon') {
      return (
        <IconButtonWithTooltip
          cypressId="btn-import-fields"
          icon={<ImportExportIcon style={{ color: '#187787' }} />}
          onClick={() => {
            handleClickOpen();
          }}
          title={`Import Fields`}
        />
      );
    }
    return (
      <Button
        id="fields-import"
        variant="outlined"
        onClick={handleClickOpen}
        color="inherit"
        style={{ minWidth: '140px' }}
        data-cy="btn-import-fields"
      >
        Import Fields
      </Button>
    );
  };

  return (
    <>
      {renderIcon()}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        PaperProps={{ style: { width: '550px' } }}
      >
        <DialogTitle id="alert-dialog-title">Import fields</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Please select a data pool to import from.
          </DialogContentText>
          <SelectPublicDataPool
            setDataPool={setFromDataPool}
            currentDataPoolId={dataPoolId}
            currentDataPoolLabel={dataPoolLabel}
            style={{ width: '100%' }}
          />
        </DialogContent>
        <DialogContent data-cy="modal-select-training-set">
          <DialogContentText id="alert-dialog-description">
            Please select a training set to import fields from.
          </DialogContentText>
          {fromDataPool?.public ? (
            <SelectPublicTrainingSet
              setTrainingSet={handleSetTrainingSet}
              dataPoolId={fromDataPool.id}
              disabled={!fromDataPool}
              inclurrentCurrentTrainingSet={false}
            />
          ) : (
            <SelectTrainingSetWithSearch
              setTrainingSet={handleSetTrainingSet}
              disabled={!fromDataPool}
              currentTrainingSetId={fromTrainingSet?.id}
              inclurrentCurrentTrainingSet={false}
              trainedOnly={false}
              width="100%"
            />
          )}
        </DialogContent>

        {loading ? (
          <LinearProgress />
        ) : (
          <DialogActions>
            <Button id="common-alert-dialog-cancel" onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button
              id="common-alert-dialog-ok"
              onClick={handleOk}
              color="primary"
              disabled={!fromDataPool || !fromTrainingSet}
              data-cy="btn-import-fields-dialog-ok"
            >
              Import
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default ImportFields;
